import React, { useContext, useEffect, useMemo, useState } from "react";
import { TextField } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CoffeeOutlinedIcon from "@mui/icons-material/CoffeeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de, enGB } from "date-fns/locale";
import { format } from "date-fns";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import { isEmployeeAccessLevel } from "../../utils/accessLevels";

import { API_BASE_URL } from "../../config";
import useToast from "../../components/useToast";
import { useTranslation } from "react-i18next";
import "../../i18n";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import { AppStatusChip } from "../../design-system/components/AppChip";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppSelect from "../../design-system/components/AppSelect";
import AppTable from "../../design-system/components/AppTable";
import AppText from "../../design-system/components/AppText";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./punch_clock.css";

const formatDuration = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(totalSeconds || 0));
  const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const formatMinutes = (minutes) => {
  const safe = Math.max(0, Math.floor(Number(minutes) || 0));
  const h = String(Math.floor(safe / 60)).padStart(2, "0");
  const m = String(safe % 60).padStart(2, "0");
  return `${h}:${m} h`;
};

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const PunchClock = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const isEmployee = isEmployeeAccessLevel(user?.access_level);
  const { t } = useTranslation();
  const { success, error } = useToast();
  const token = localStorage.getItem("session_token");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [locations, setLocations] = useState([]);
  const [selectedWorkingLocationId, setSelectedWorkingLocationId] = useState("");
  const [loadedAt, setLoadedAt] = useState(new Date());
  const [now, setNow] = useState(new Date());

  const [openManualDialog, setOpenManualDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openCheckoutBreakDialog, setOpenCheckoutBreakDialog] = useState(false);
  const [openMissedOutDialog, setOpenMissedOutDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [manualEntryBreakTime, setManualEntryBreakTime] = useState("");
  const [checkInLocationId, setCheckInLocationId] = useState("");
  const [manualEntryLocationId, setManualEntryLocationId] = useState("");
  const [manualEntryUserId, setManualEntryUserId] = useState("");
  const [checkoutBreakMinutes, setCheckoutBreakMinutes] = useState("");
  const [missedPunchOutTime, setMissedPunchOutTime] = useState(new Date());
  const [missedPunchOutBreakTime, setMissedPunchOutBreakTime] = useState("");

  const activeEntry = data.active_entry || null;
  const activeCheckIn = parseDate(activeEntry?.check_in);
  const manualBreak = data.manual_break === 1 || data.manual_break === "1" || data.manual_break === true;

  const activeElapsedSeconds = useMemo(() => {
    if (!activeCheckIn) return 0;
    return Math.max((now.getTime() - activeCheckIn.getTime()) / 1000, 0);
  }, [activeCheckIn, now]);

  const today = data.today || {};
  const liveWorkMinutes = useMemo(() => {
    const base = Number(today.work_minutes || 0);
    if (!activeEntry) return base;
    return base + Math.max(Math.floor((now.getTime() - loadedAt.getTime()) / 60000), 0);
  }, [activeEntry, loadedAt, now, today.work_minutes]);

  const targetMinutes = Number(today.target_minutes || 480);
  const progress = targetMinutes ? Math.min(Math.round((liveWorkMinutes / targetMinutes) * 100), 100) : 0;
  const recentBookings = Array.isArray(data.recent_bookings) ? data.recent_bookings : [];
  const pendingEntries = Array.isArray(data.pending_entries) ? data.pending_entries : [];

  const loadData = async (locationId = selectedWorkingLocationId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/web/punch-clock`, {
        params: locationId ? { location_id: locationId } : undefined,
        headers: { Authorization: `Bearer ${token}` },
      });
      const nextData = res.data || {};
      setData(nextData);
      setLocations(Array.isArray(nextData.locations) ? nextData.locations : []);
      setSelectedWorkingLocationId(
        nextData.selected_location_id ? String(nextData.selected_location_id) : ""
      );
      setLoadedAt(new Date());
    } catch (err) {
      error(err?.response?.data?.msg || t("notification.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const postPunch = async (
    payload,
    successMessage,
    locationId = selectedWorkingLocationId
  ) => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/web/punch-clock`, {
        ...payload,
        location_id: locationId || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      success(successMessage || t("notification.successful"));
      await loadData(locationId);
    } catch (err) {
      error(err?.response?.data?.msg || t("notification.error"));
      setLoading(false);
    }
  };

  const checkIn = () => {
    if (locations.length <= 1) {
      postPunch({ button: "Check In" });
      return;
    }
    setCheckInLocationId(selectedWorkingLocationId);
    setOpenLocationDialog(true);
  };

  const submitCheckIn = async () => {
    setOpenLocationDialog(false);
    setSelectedWorkingLocationId(checkInLocationId);
    await postPunch({ button: "Check In" }, undefined, checkInLocationId);
  };

  const openManualEntry = () => {
    setManualEntryLocationId(selectedWorkingLocationId);
    setManualEntryUserId(currentUserId);
    setOpenManualDialog(true);
  };

  const changeManualEntryLocation = (locationId) => {
    setManualEntryLocationId(locationId);
    // Wer am neuen Standort nicht zugeordnet ist, bleibt nicht ausgewählt.
    const members = teamMembersByLocation.get(String(locationId)) || [];
    if (!members.some((member) => String(member.id) === manualEntryUserId)) {
      setManualEntryUserId(currentUserId);
    }
  };

  const handleCheckOut = () => {
    if (manualBreak) {
      setCheckoutBreakMinutes("");
      setOpenCheckoutBreakDialog(true);
      return;
    }
    submitCheckOut(0);
  };

  const submitCheckOut = async (breakTime) => {
    setOpenCheckoutBreakDialog(false);
    await postPunch({ button: "Check Out", manual_break_time: breakTime || 0 });
  };

  const deletePunch = async () => {
    setOpenDeleteDialog(false);
    await postPunch({ button: "Deletion" });
  };

  const submitManualEntry = async () => {
    const forTeamMember = Boolean(manualEntryUserId) && manualEntryUserId !== currentUserId;
    const payload = {
      start: format(startDate, "dd.MM.yyyy HH:mm"),
      end: format(endDate, "dd.MM.yyyy HH:mm"),
      manual_break_time: manualBreak ? manualEntryBreakTime || 0 : 0,
      ...(forTeamMember ? { user_id: Number(manualEntryUserId) } : {}),
    };
    setOpenManualDialog(false);
    setSelectedWorkingLocationId(manualEntryLocationId);
    await postPunch(
      payload,
      forTeamMember ? t("punchclock.team_entry_saved") : undefined,
      manualEntryLocationId
    );
  };

  const submitMissedOut = async () => {
    const payload = {
      manual_punch_out: format(missedPunchOutTime, "dd.MM.yyyy HH:mm"),
      manual_break_time: manualBreak ? missedPunchOutBreakTime || 0 : 0,
    };
    setOpenMissedOutDialog(false);
    await postPunch(payload, t("punchclock.missed_checkout_saved") || "Ausstempelzeit gespeichert");
  };

  const statusSubtext = activeEntry
    ? t("punchclock.checked_in_at", { time: activeEntry.check_in_time || data.punch_clock_time || "" })
    : t("punchclock.no_active");
  const locationOptions = useMemo(
    () => locations.map((location) => ({
      value: String(location.id),
      label: location.name,
    })),
    [locations]
  );
  const currentUserId = data.current_user_id ? String(data.current_user_id) : "";
  // Nur Standorte, an denen die Planung Einträge für andere erfassen darf.
  const teamMembersByLocation = useMemo(
    () => new Map(
      (Array.isArray(data.team_entry_locations) ? data.team_entry_locations : []).map((entry) => [
        String(entry.location_id),
        Array.isArray(entry.members) ? entry.members : [],
      ])
    ),
    [data.team_entry_locations]
  );
  const manualEntryEmployeeOptions = useMemo(() => {
    const manualLocationId = manualEntryLocationId || selectedWorkingLocationId;
    const members = teamMembersByLocation.get(String(manualLocationId));
    if (!members || !currentUserId) return [];
    const others = members
      .filter((member) => String(member.id) !== currentUserId)
      .map((member) => ({ value: String(member.id), label: member.name }));
    return [{ value: currentUserId, label: data.current_user_name || "" }, ...others];
  }, [currentUserId, data.current_user_name, manualEntryLocationId, selectedWorkingLocationId, teamMembersByLocation]);
  const bookingColumns = useMemo(() => [
    { key: "date_label", label: t("date"), minWidth: 104, grow: 0.9 },
    { key: "time", label: t("punchclock.time"), minWidth: 80, grow: 0.7 },
    {
      key: "label",
      label: t("punchclock.type"),
      minWidth: 132,
      grow: 1.2,
      render: (value, booking) => (
        <span className="punch-clock__booking-type">
          <span className={`punch-clock__booking-dot${booking.type === "check_in" ? " is-check-in" : ""}`} />
          <AppText variant="body-1">{value}</AppText>
        </span>
      ),
    },
    {
      key: "source",
      label: t("punchclock.source"),
      minWidth: 92,
      grow: 0.8,
      sortable: false,
      render: () => (
        <span className="punch-clock__booking-source">
          <SmartphoneOutlinedIcon />
          <AppText variant="body-2" tone="secondary">Web</AppText>
        </span>
      ),
    },
  ], [t]);

  return (
    <div className="punch-clock-page">
      <AppWorkspace title={t("punchclock.title") || "Stempeluhr"} loading={loading}>

      <div className="punch-clock__content">
        <AppCard className="punch-clock__primary-card">
          <AppCardHeader
            title={t("punchclock.time_tracking")}
            visual={<AccessTimeIcon />}
          />
          <AppCardBody>
            <div className="punch-clock__hero">
              <div className="punch-clock__time-block">
                <AppText as="p" variant="body-1" tone="secondary">{statusSubtext}</AppText>
                <AppText as="span" variant="body-2" tone="secondary">
                  {activeEntry ? t("punchclock.running_work_time") : t("punchclock.work_time_today")}
                </AppText>
                <output className="punch-clock__time" aria-live="polite">
                  {activeEntry ? formatDuration(activeElapsedSeconds) : formatMinutes(liveWorkMinutes).replace(" h", "")}
                </output>
              </div>

              <div className="punch-clock__actions">
                <AppButton className="punch-clock__main-action" variant="neutral" size="lg" onClick={activeEntry ? handleCheckOut : checkIn}>
                  {activeEntry ? <LogoutRoundedIcon /> : <LoginRoundedIcon />}
                  {activeEntry ? t("button.check_out") : t("button.check_in")}
                </AppButton>
                {activeEntry ? (
                  <AppButton variant="outlined" size="md" onClick={() => setOpenMissedOutDialog(true)}>
                    <ScheduleOutlinedIcon />
                    {t("punchclock.manual_checkout")}
                  </AppButton>
                ) : null}
                <AppButton variant="outlined" size="md" onClick={openManualEntry}>
                  <AddCircleOutlineIcon />
                  {t("punchclock.create_entry")}
                </AppButton>
                {activeEntry ? (
                  <AppButton variant="outlined" size="md" onClick={() => setOpenDeleteDialog(true)}>
                    <DeleteOutlineIcon />
                    {t("punchclock.delete_entry")}
                  </AppButton>
                ) : null}
              </div>
            </div>
          </AppCardBody>
        </AppCard>

        <div className="punch-clock__overview-grid">
          <InfoCard icon={<CalendarMonthOutlinedIcon />} title={t("punchclock.today_overview")}>
            <div className="punch-clock__metrics">
              <MetricRow icon={<AccessTimeIcon />} label={t("punchclock.work_time_today")} value={formatMinutes(liveWorkMinutes)} accent />
              <MetricRow icon={<CoffeeOutlinedIcon />} label={t("punchclock.break")} value={formatMinutes(today.break_minutes || 0)} />
              <MetricRow icon={<CheckCircleOutlineIcon />} label={t("punchclock.target_time")} value={formatMinutes(targetMinutes)} />
            </div>
            <div
              className="punch-clock__progress"
              role="progressbar"
              aria-label={t("punchclock.target_progress")}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={progress}
            >
              <span style={{ width: `${progress}%` }} />
            </div>
            <div className="punch-clock__progress-label">
              <AppText variant="body-2" tone="secondary">{t("punchclock.target_progress")}</AppText>
              <AppText className="punch-clock__brand-value" variant="body-2">{progress} %</AppText>
            </div>
          </InfoCard>

          <InfoCard
            icon={<AccessTimeIcon />}
            title={t("punchclock.recent_bookings")}
            bodyInset="none"
            action={<AppButton variant="muted" size="sm" onClick={() => navigate("/report")}>{t("punchclock.show_all")}</AppButton>}
          >
            <AppTable
              columns={bookingColumns}
              rows={recentBookings}
              showFooter={false}
              emptyState={t("punchclock.no_bookings")}
              className="punch-clock__bookings-table"
            />
          </InfoCard>

          <InfoCard
            icon={<CheckCircleOutlineIcon />}
            title={t("punchclock.pending_requests")}
            /* Mitarbeitende haben keine Genehmigungsseite; die Liste zeigt ihre offenen Stempelungen schon. */
            action={isEmployee ? null : <AppButton variant="muted" size="sm" onClick={() => navigate("/approval/flow")}>{t("punchclock.show_all")}</AppButton>}
          >
            {pendingEntries.length ? (
              <div className="punch-clock__pending-list">
                {pendingEntries.map((entry) => (
                  <div className="punch-clock__pending-row" key={entry.id}>
                    <div>
                      <AppText as="strong" variant="body-1">{entry.date_label}</AppText>
                      <AppText as="span" variant="body-2" tone="secondary">
                        {entry.start} – {entry.end}
                      </AppText>
                    </div>
                    <AppStatusChip status="warning" size="sm">{t("punchclock.open")}</AppStatusChip>
                  </div>
                ))}
              </div>
            ) : (
              <AppCallout
                variant="success"
                title={t("punchclock.all_done")}
                description={t("punchclock.no_pending_requests")}
              />
            )}
          </InfoCard>
        </div>
      </div>

      <ManualEntryDialog
        open={openManualDialog}
        onClose={() => setOpenManualDialog(false)}
        title={t("punchclock.manual_title") || "Manuellen Eintrag erstellen"}
        submitLabel={t("button.submit") || "Speichern"}
        startDate={startDate}
        endDate={endDate}
        onStartChange={setStartDate}
        onEndChange={setEndDate}
        breakEnabled={manualBreak}
        breakValue={manualEntryBreakTime}
        onBreakChange={setManualEntryBreakTime}
        onSubmit={submitManualEntry}
        locationOptions={locationOptions}
        locationValue={manualEntryLocationId}
        onLocationChange={changeManualEntryLocation}
        employeeOptions={manualEntryEmployeeOptions}
        employeeValue={manualEntryUserId}
        onEmployeeChange={setManualEntryUserId}
      />

      <AppDialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)} size="sm">
        <AppDialogHeader
          title={t("punchclock.select_working_location")}
          visual={<LoginRoundedIcon />}
        />
        <AppDialogBody>
          <AppField
            label={t("punchclock.working_location")}
            description={t("punchclock.select_working_location_description")}
            size="md"
            layout="stacked"
            required
          >
            <AppSelect
              value={checkInLocationId}
              options={locationOptions}
              onChange={(event) => setCheckInLocationId(event.target.value)}
            />
          </AppField>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" onClick={() => setOpenLocationDialog(false)}>
            {t("button.cancel")}
          </AppButton>
          <AppButton variant="primary" disabled={!checkInLocationId} onClick={submitCheckIn}>
            {t("button.check_in")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>

      <AppDialog open={openCheckoutBreakDialog} onClose={() => setOpenCheckoutBreakDialog(false)} size="sm">
        <AppDialogHeader title={t("punchclock.enter_break_title")} visual={<CoffeeOutlinedIcon />} />
        <AppDialogBody>
          <AppField label={t("punchclock.break_time")} size="md" layout="stacked">
            <AppInput
              type="number"
              min="0"
              value={checkoutBreakMinutes}
              trailingText="Min."
              onChange={(event) => setCheckoutBreakMinutes(event.target.value)}
            />
          </AppField>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" onClick={() => setOpenCheckoutBreakDialog(false)}>{t("button.cancel")}</AppButton>
          <AppButton variant="primary" onClick={() => submitCheckOut(checkoutBreakMinutes)}>{t("button.confirm")}</AppButton>
        </AppDialogFooter>
      </AppDialog>

      <ManualEntryDialog
        open={openMissedOutDialog}
        onClose={() => setOpenMissedOutDialog(false)}
        title={t("punchclock.missed_checkout_title") || "Manuell ausstempeln"}
        submitLabel={t("button.submit") || "Speichern"}
        startDate={null}
        endDate={missedPunchOutTime}
        onEndChange={setMissedPunchOutTime}
        breakEnabled={manualBreak}
        breakValue={missedPunchOutBreakTime}
        onBreakChange={setMissedPunchOutBreakTime}
        onSubmit={submitMissedOut}
        endLabel={t("punchclock.checkout_time_label")}
        hideStart
      />

      <AppConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={deletePunch}
        title={t("punchclock.delete_active_title")}
        description={t("punchclock.delete_active_description")}
        icon={<DeleteOutlineIcon />}
        confirmIcon={<DeleteOutlineIcon />}
        confirmLabel="Löschen"
        confirmTone="error"
        cancelLabel={t("button.cancel")}
      />
      </AppWorkspace>
    </div>
  );
};

const InfoCard = ({ icon, title, action = null, children, bodyInset = "default" }) => (
  <AppCard className="punch-clock__info-card">
    <AppCardHeader title={title} visual={icon} action={action} titleVariant="compact" />
    <AppCardBody inset={bodyInset}>{children}</AppCardBody>
  </AppCard>
);

const MetricRow = ({ icon, label, value, accent = false }) => (
  <div className={`punch-clock__metric${accent ? " is-accent" : ""}`}>
    <span className="punch-clock__metric-label">
      <span className="punch-clock__metric-icon" aria-hidden="true">{icon}</span>
      <AppText variant="body-1" tone="secondary">{label}</AppText>
    </span>
    <AppText as="strong" variant="heading-3">{value}</AppText>
  </div>
);

const ManualEntryDialog = ({
  open,
  onClose,
  title,
  submitLabel,
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  breakEnabled,
  breakValue,
  onBreakChange,
  onSubmit,
  locationOptions = [],
  locationValue,
  onLocationChange,
  employeeOptions = [],
  employeeValue,
  onEmployeeChange,
  endLabel,
  hideStart = false,
}) => {
  const { t, i18n } = useTranslation();
  const calendarLocale = i18n.language?.startsWith("en") ? enGB : de;

  return (
    <AppDialog open={open} onClose={onClose} size="sm">
    <AppDialogHeader title={title} visual={<ScheduleOutlinedIcon />} />
    <AppDialogBody>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={calendarLocale}>
        <AppFieldStack gap="md">
          {!hideStart && locationOptions.length > 1 ? (
            <AppField
              label={t("punchclock.working_location")}
              description={t("punchclock.select_working_location_description")}
              size="md"
              layout="stacked"
              required
            >
              <AppSelect
                value={locationValue}
                options={locationOptions}
                onChange={(event) => onLocationChange(event.target.value)}
              />
            </AppField>
          ) : null}
          {!hideStart && employeeOptions.length > 1 ? (
            <AppField
              label={t("punchclock.employee")}
              description={t("punchclock.employee_description")}
              size="md"
              layout="stacked"
              required
            >
              <AppSelect
                value={employeeValue}
                options={employeeOptions}
                onChange={(event) => onEmployeeChange(event.target.value)}
                searchable
                searchPlaceholder={t("team.select_user")}
                aria-label={t("punchclock.employee")}
              />
            </AppField>
          ) : null}
          {!hideStart ? (
            <AppField label={t("punchclock.start")} size="md" layout="stacked">
              <DateTimePicker
                ampm={false}
                value={startDate}
                onChange={onStartChange}
                format="dd.MM.yyyy HH:mm"
                renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="dd.mm.yyyy hh:mm" />}
              />
            </AppField>
          ) : null}
          <AppField label={endLabel || t("punchclock.end")} size="md" layout="stacked">
            <DateTimePicker
              ampm={false}
              value={endDate}
              onChange={onEndChange}
              format="dd.MM.yyyy HH:mm"
              renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="dd.mm.yyyy hh:mm" />}
            />
          </AppField>
          {breakEnabled ? (
            <AppField label={t("punchclock.break_time")} size="md" layout="stacked">
              <AppInput
                type="number"
                min="0"
                value={breakValue}
                trailingText="Min."
                onChange={(event) => onBreakChange(event.target.value)}
              />
            </AppField>
          ) : null}
        </AppFieldStack>
      </LocalizationProvider>
    </AppDialogBody>
    <AppDialogFooter>
      <AppButton variant="neutral" onClick={onClose}>{t("button.cancel")}</AppButton>
      <AppButton
        variant="primary"
        disabled={locationOptions.length > 1 && !locationValue}
        onClick={onSubmit}
      >
        {submitLabel}
      </AppButton>
    </AppDialogFooter>
    </AppDialog>
  );
};

export default PunchClock;
